import ELK, {ElkExtendedEdge, ELK as ELKInterface} from 'elkjs/lib/elk.bundled';
import {Edge, Node, NodeOrigin} from 'reactflow';

import {assert} from 'Asserts';

import {EdgeType} from '../edges/edgeOptions';
import {FlowchartData} from './algorithms';
import {DimensionsForLayout} from './calculateDimensionsForLayout';
import {unhideFlowchartElementAfterLayout} from './hideFlowchartElementBeforeLayoutUtils';
import translateNodeOrigin from './translateNodeOrigin';

// ELK lays out nodes by the top-left of each node.
const ELK_NODE_ORIGIN: NodeOrigin = [0, 0];

// Controls how nodes behave during ELK layout.
const ELK_NODE_PROPERTIES = {
  // Handles are specified using their x/y positions relative to the node.
  'org.eclipse.elk.portConstraints': 'FIXED_POS',
};

const ELK_LAYOUT_OPTIONS = {
  'elk.algorithm': 'layered',
  // Left to right layout
  'elk.direction': 'RIGHT',
  // Create curved edges (splines)
  'elk.edgeRouting': 'SPLINES',
  // Allow edges to look more orthogonal to avoid nodes.
  'elk.layered.edgeRouting.splines.mode': 'CONSERVATIVE',
  // Don't merge edges.
  'elk.layered.mergeEdges': 'false',
  // keep some space between edges.
  'elk.spacing.edgeEdge': '20',
  // keep some space between edges between layers.
  'elk.layered.spacing.edgeEdgeBetweenLayers': '20',
  // Process connected components together so we can more easily enforce ordering when the graph is disconnected.
  'elk.separateConnectedComponents': 'false',
  // Place edge labels inline on the edge (hint that might not always be respected).
  'elk.edgeLabels.inline': 'true',
  // Place edge labels at the center of the edge (hint that might not always be respected).
  'elk.edgeLabels.placement': 'CENTER',
};

let elk: ELKInterface;

function validateStartAndEndNodes<N extends Node>(
  nodes: Array<N>,
  startNodeId?: string,
  endNodeId?: string
): void {
  assert(
    startNodeId == null || endNodeId == null || startNodeId !== endNodeId,
    'Start and end node IDs cannot be the same'
  );
  assert(
    startNodeId == null || nodes.find((node) => node.id === startNodeId) != null,
    'Start node ID does not exist'
  );
  assert(
    endNodeId == null || nodes.find((node) => node.id === endNodeId) != null,
    'End node ID does not exist'
  );
}

function maybeGetStartNodeOrderingProperty(testNodeId: string, startNodeId?: string): Record<string, string> {
  return startNodeId != null && startNodeId === testNodeId
    ? {
        'org.eclipse.elk.layered.layering.layerConstraint': 'FIRST_SEPARATE',
      }
    : {};
}

function maybeGetEndNodeOrderingProperty(testNodeId: string, endNodeId?: string): Record<string, string> {
  return endNodeId != null && endNodeId === testNodeId
    ? {
        'org.eclipse.elk.layered.layering.layerConstraint': 'LAST_SEPARATE',
      }
    : {};
}

const elkLayout = async <N extends Node, E extends Edge>(
  {nodes, edges, getHandles}: FlowchartData<N, E>,
  dimensions: DimensionsForLayout
): Promise<FlowchartData<N, E>> => {
  const allHandleIds: Array<string> = [];
  const {startNodeId, endNodeId} = dimensions;
  validateStartAndEndNodes(nodes, startNodeId, endNodeId);

  // Convert nodes and edges to ELK format
  const elkNodes = nodes.map((node) => {
    assert(dimensions.nodes[node.id] != null, 'Node is missing dimensions needed for layout');
    const {height, width} = dimensions.nodes[node.id];
    const handles = getHandles?.(node.id) ?? [];
    allHandleIds.push(...handles.map((handle) => handle.id ?? ''));

    const elkStartNodeProperty = maybeGetStartNodeOrderingProperty(node.id, startNodeId);
    const elkEndNodeProperty = maybeGetEndNodeOrderingProperty(node.id, endNodeId);

    return {
      id: node.id,
      width,
      height,
      properties: {
        ...ELK_NODE_PROPERTIES,
        ...elkStartNodeProperty,
        ...elkEndNodeProperty,
      },
      ports: handles.map((handle) => ({
        id: handle.id ?? '',
        x: handle.x,
        y: handle.y,
        width: handle.width,
        height: handle.height,
      })),
    };
  });

  const elkEdges = edges.map((edge) => {
    assert(dimensions.edges[edge.id] != null, 'Edge is missing dimensions needed for layout');

    // Only validate handles if they are specified
    if (edge.sourceHandle != null) {
      assert(
        allHandleIds.includes(edge.sourceHandle),
        `Source handle ${edge.sourceHandle} for edge ${edge.id} not found on node.`
      );
    }
    if (edge.targetHandle != null) {
      assert(
        allHandleIds.includes(edge.targetHandle),
        `Target handle ${edge.targetHandle} for edge ${edge.id} not found on node.`
      );
    }

    return {
      id: edge.id,
      sources: [edge.sourceHandle ?? edge.source],
      targets: [edge.targetHandle ?? edge.target],
      labels:
        edge.label != null && typeof edge.label === 'string'
          ? [
              {
                text: edge.label,
                width: dimensions.edges[edge.id].width,
                height: dimensions.edges[edge.id].height,
              },
            ]
          : [],
    };
  });

  // Configure ELK layout
  const elkGraph = {
    id: 'root',
    children: elkNodes,
    edges: elkEdges,
    layoutOptions: {
      ...ELK_LAYOUT_OPTIONS,
      'elk.spacing.nodeNode': String(dimensions.verticalSpaceBetweenNodes),
      'elk.layered.spacing.nodeNodeBetweenLayers': String(dimensions.horizontalSpaceBetweenNodes),
      'elk.layered.spacing.edgeNodeBetweenLayers': String(dimensions.horizontalSpaceBetweenNodes),
    },
  };

  // Run ELK layout
  elk ??= new ELK();
  const layoutedGraph = await elk.layout(elkGraph);

  // Apply calculated positions, and un-hide any elements that were temporarily hidden.
  const elkNodesById = new Map(layoutedGraph.children?.map((child) => [child.id, child]) || []);
  const elkEdgesById = new Map(layoutedGraph.edges?.map((edge) => [edge.id, edge as ElkExtendedEdge]) || []);

  return {
    nodes: nodes.map((node) => {
      const elkNode = elkNodesById.get(node.id);
      assert(elkNode != null, `ELK layout did not return position for node ${node.id}`);
      return unhideFlowchartElementAfterLayout({
        ...node,
        position: translateNodeOrigin({
          sourceNodeOrigin: ELK_NODE_ORIGIN,
          sourcePosition: {x: elkNode.x ?? 0, y: elkNode.y ?? 0},
          targetNodeOrigin: dimensions.nodeOrigin,
          nodeHeight: node.height ?? 0,
          nodeWidth: node.width ?? 0,
        }),
      });
    }),
    edges: edges.map((edge) => {
      const elkEdge = elkEdgesById.get(edge.id);
      assert(
        elkEdge != null && elkEdge.sections != null && elkEdge.sections.length > 0,
        `ELK layout did not return edge data for edge ${edge.id}`
      );

      return unhideFlowchartElementAfterLayout({
        ...edge,
        data: {
          ...edge.data,
          type: EdgeType.ELK_SPLINE,
          elkEdgeData: {
            sections: elkEdge.sections,
            labels: elkEdge.labels,
          },
        },
      });
    }),
  };
};

export default elkLayout;
